import React from 'react';
import { Heart, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, MessageCircle } from 'lucide-react';

const Footer: React.FC = () => {
  const socialLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://facebook.com/lifeshare',
      color: 'hover:text-blue-600',
      bgColor: 'hover:bg-blue-50'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: 'https://twitter.com/lifeshare',
      color: 'hover:text-sky-500',
      bgColor: 'hover:bg-sky-50'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://instagram.com/lifeshare',
      color: 'hover:text-pink-600',
      bgColor: 'hover:bg-pink-50'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: 'https://linkedin.com/company/lifeshare',
      color: 'hover:text-blue-700',
      bgColor: 'hover:bg-blue-50'
    },
    {
      name: 'YouTube',
      icon: Youtube,
      url: 'https://youtube.com/@lifeshare',
      color: 'hover:text-red-600',
      bgColor: 'hover:bg-red-50'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: 'https://wa.me/15551234567',
      color: 'hover:text-green-600',
      bgColor: 'hover:bg-green-50'
    }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-600 rounded-lg shadow-md">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">LifeShare</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed font-medium">
              Connecting donors, recipients, and hospitals to save lives through organ donation.
            </p>
            
            {/* Social Media Links */}
            <div className="pt-4">
              <h4 className="text-sm font-bold text-white mb-3">Follow Us</h4>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group p-2 rounded-lg bg-gray-800 border border-gray-700 transition-all duration-300 transform hover:scale-110 hover:shadow-lg ${social.bgColor} ${social.color}`}
                      aria-label={`Follow us on ${social.name}`}
                    >
                      <Icon className="h-5 w-5 text-gray-300 group-hover:text-current transition-colors duration-300" />
                    </a>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Stay connected for updates, stories, and support
              </p>
            </div>
            
            {/* Bolt.new Logo */}
            <div className="pt-4 border-t border-gray-700">
              <a 
                href="https://bolt.new" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity duration-300"
              >
                <img 
                  src="/white_circle_360x360.png" 
                  alt="Powered by Bolt.new" 
                  className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300"
                />
              </a>
              <p className="text-xs text-gray-400 mt-2">
                <a 
                  href="https://bolt.new" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors duration-300"
                >
                  Powered by Bolt.new
                </a>
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white border-b-2 border-blue-600 pb-2 inline-block">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a href="/" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Home
                </a>
              </li>
              <li>
                <a href="/register-donor" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Become a Donor
                </a>
              </li>
              <li>
                <a href="/education" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Education
                </a>
              </li>
              <li>
                <a href="/about" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  About Us
                </a>
              </li>
              <li>
                <a href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Sign In
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white border-b-2 border-green-600 pb-2 inline-block">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="/education" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  FAQs
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Contact Support
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm font-medium hover:underline">
                  Press Kit
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-white border-b-2 border-purple-600 pb-2 inline-block">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-800 transition-colors">
                <div className="p-1 bg-blue-600 rounded">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <a 
                  href="mailto:support@lifeshare.org"
                  className="text-gray-300 text-sm font-medium hover:text-white transition-colors"
                >
                  support@lifeshare.org
                </a>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-800 transition-colors">
                <div className="p-1 bg-green-600 rounded">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <a 
                  href="tel:+18005433728"
                  className="text-gray-300 text-sm font-medium hover:text-white transition-colors"
                >
                  1-800-LIFESAVE
                </a>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-800 transition-colors">
                <div className="p-1 bg-purple-600 rounded">
                  <MapPin className="h-4 w-4 text-white" />
                </div>
                <span className="text-gray-300 text-sm font-medium">24/7 Emergency Support</span>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="pt-4 border-t border-gray-700">
              <h4 className="text-sm font-bold text-white mb-3">Stay Updated</h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-l-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-r-lg transition-colors">
                  <Mail className="h-4 w-4 text-white" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Get updates on organ donation news and stories
              </p>
            </div>
          </div>
        </div>

        <div className="border-t-2 border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm font-medium">
              © 2024 LifeShare. All rights reserved. Medical platform compliant with HIPAA and GDPR.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm font-medium hover:underline">
                Privacy
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm font-medium hover:underline">
                Terms
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm font-medium hover:underline">
                Accessibility
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm font-medium hover:underline">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;